import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { fetchKoPendientes } from "@/lib/cobertura";
import { PageHeader, ScopeNotice, useScopeGuard } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Upload, Loader2, Check, X, RefreshCw, AlertTriangle, FileText, Star } from "lucide-react";
import { toast } from "sonner";
import { OnboardingAsistido } from "@/components/OnboardingAsistido";

const TIPOS = ["POL", "PRO", "REG", "INS", "FRM", "APPCC", "Manual", "Otro"];
const AVISO_LABEL = {
  fecha_vencida: "Fecha vencida",
  sin_texto_legible: "Sin texto legible",
  formato_no_soportado: "Formato no soportado",
  error_parseo: "No se pudo leer",
  convertidor_no_disponible: "Convertidor no disponible · reintenta",
  no_parece_documento_de_calidad: "No parece documento de calidad",
  sin_fecha_de_revision: "Sin fecha de revisión",
  documento_truncado: "Solo se analizó parte del documento",
};
const avisoColor = (t) =>
  t === "convertidor_no_disponible"
    ? "bg-[#2563EB] hover:bg-[#2563EB]"
    : (["fecha_vencida", "sin_texto_legible", "error_parseo", "formato_no_soportado", "no_parece_documento_de_calidad"].includes(t)
        ? "bg-[#EF4444] hover:bg-[#EF4444]"
        : "bg-[#F59E0B] hover:bg-[#F59E0B]");

function Revision({ sub, onDone, nav }) {
  const p = sub.propuesta || {};
  const [form, setForm] = useState({
    titulo: p.titulo_normalizado || "", tipo: p.tipo_sugerido || "Otro",
    codigo: p.codigo_sugerido || "", fecha_revision: p.fecha_revision_detectada || "",
    responsable: p.responsable_detectado || "",
  });
  const [reqs, setReqs] = useState((p.requisitos_sugeridos || []).map((r) => ({ ...r, sel: true })));
  const toggle = (n) => setReqs((rs) => rs.map((r) => r.numero === n ? { ...r, sel: !r.sel } : r));

  const confirmar = async () => {
    try {
      await api.post(`/documentos/subidas/${sub.id}/confirmar`, {
        ...form, requisitos: reqs.filter((r) => r.sel).map((r) => r.numero),
      });
      toast.success("Documento creado desde propuesta IA");
      onDone();
      const n = await fetchKoPendientes(api);
      if (n > 0) {
        toast(`Te quedan ${n} ${n === 1 ? "KO pendiente" : "KO pendientes"} de tu confirmación`, {
          description: "Revísalos y confírmalos en el Mapa Normativo.",
          action: { label: "Revisar", onClick: () => nav?.("/norma") },
        });
      }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const descartar = async () => { await api.post(`/documentos/subidas/${sub.id}/descartar`); toast.success("Descartada"); onDone(); };
  const reanalizar = async () => { await api.post(`/documentos/subidas/${sub.id}/reanalizar`); toast.success("Reanalizando…"); onDone(); };
  const avisoInc = async (a) => {
    await api.post(`/documentos/subidas/${sub.id}/aviso-incidencia`, { tipo: a.tipo, detalle: a.detalle });
    toast.success("Incidencia creada desde aviso");
  };

  return (
    <Card className="p-5 border-[#E2E8F0]" data-testid={`revision-${sub.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-[#0E2A47]/50">{sub.filename}</span>
          <Badge variant="outline" className="border-[#FF5625] text-[#FF5625] text-[10px]"><Sparkles size={10} className="mr-1" />IA {p.confianza_global_pct ?? 0}%</Badge>
          {p.idioma_detectado && <Badge variant="outline" data-testid={`idioma-analisis-${sub.id}`} className="border-[#0E2A47]/20 text-[#0E2A47]/70 text-[10px] uppercase">Idioma: {p.idioma_detectado}</Badge>}
          {p.sector_label && <Badge variant="outline" data-testid={`sector-analisis-${sub.id}`} className="border-[#0E2A47]/20 text-[#0E2A47]/70 text-[10px]">Sector: {p.sector_label}</Badge>}
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={reanalizar} data-testid={`reanalizar-${sub.id}`}><RefreshCw size={14} /></Button>
        </div>
      </div>

      {(p.avisos || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {p.avisos.map((a, i) => (
            <button key={i} onClick={() => avisoInc(a)} title="Convertir en incidencia" data-testid={`aviso-${sub.id}-${a.tipo}`}>
              <Badge className={`${avisoColor(a.tipo)} text-[10px]`}><AlertTriangle size={10} className="mr-1" />{AVISO_LABEL[a.tipo] || a.tipo}</Badge>
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[10px] font-bold uppercase text-[#0E2A47]/60">Título</Label>
          <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} data-testid={`rev-titulo-${sub.id}`} className="mt-1 h-9" /></div>
        <div><Label className="text-[10px] font-bold uppercase text-[#0E2A47]/60">Tipo</Label>
          <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
            <SelectTrigger className="mt-1 h-9" data-testid={`rev-tipo-${sub.id}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-[10px] font-bold uppercase text-[#0E2A47]/60">Código</Label>
          <Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} data-testid={`rev-codigo-${sub.id}`} className="mt-1 h-9" /></div>
        <div><Label className="text-[10px] font-bold uppercase text-[#0E2A47]/60">Fecha revisión</Label>
          <Input value={form.fecha_revision || ""} onChange={(e) => setForm({ ...form, fecha_revision: e.target.value })} data-testid={`rev-fecha-${sub.id}`} className="mt-1 h-9" placeholder="YYYY-MM-DD" /></div>
        <div className="col-span-2"><Label className="text-[10px] font-bold uppercase text-[#0E2A47]/60">Responsable</Label>
          <Input value={form.responsable || ""} onChange={(e) => setForm({ ...form, responsable: e.target.value })} data-testid={`rev-responsable-${sub.id}`} className="mt-1 h-9" /></div>
      </div>

      <div className="mt-3">
        <Label className="text-[10px] font-bold uppercase text-[#0E2A47]/60">Mapeos sugeridos</Label>
        {reqs.length === 0 ? <div className="text-xs text-[#0E2A47]/40 mt-1">Sin mapeos propuestos.</div> : (
          <div className="mt-1 space-y-1">
            {reqs.map((r) => (
              <label key={r.numero} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`rev-req-${sub.id}-${r.numero}`}>
                <Checkbox checked={r.sel} onCheckedChange={() => toggle(r.numero)} />
                <span className="font-mono text-xs text-[#FF5625]">{r.numero}</span>
                <Badge variant="outline" className="text-[10px] border-[#E2E8F0]">{r.confianza_pct}%</Badge>
                <span className="text-[#0E2A47]/70 text-xs truncate">{r.motivo_breve}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-4">
        <Button onClick={confirmar} data-testid={`confirmar-${sub.id}`} className="bg-[#10B981] hover:bg-[#0f9d74]"><Check size={15} className="mr-1" />Confirmar y crear</Button>
        <Button onClick={descartar} variant="outline" data-testid={`descartar-${sub.id}`} className="border-[#EF4444] text-[#EF4444]"><X size={15} className="mr-1" />Descartar</Button>
      </div>
    </Card>
  );
}

export default function SubidaIA() {
  const { user, tenant, reload, scopeTenant } = useAuth();
  const needsScope = useScopeGuard(user, scopeTenant);
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [files, setFiles] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [reco, setReco] = useState(null);
  const [sectores, setSectores] = useState([]);
  const [sector, setSector] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const tid = tenant?.id || scopeTenant;
  const puedeEditarSector = user.rol === "admin" || user.rol === "superadmin";

  const load = useCallback(async () => {
    if (needsScope) return;
    const { data } = await api.get("/documentos/subidas");
    setSubs(data);
  }, [needsScope]);

  const loadReco = useCallback(async () => {
    if (needsScope) return;
    try {
      const [r, s] = await Promise.all([
        api.get("/documentos/recomendados"),
        api.get("/registro/sectores"),
      ]);
      setReco(r.data);
      setSectores(s.data);
      setSector(r.data.sector_doctrina || "");
    } catch (e) { /* recomendaciones son opcionales, no bloquean la subida */ }
  }, [needsScope]);

  useEffect(() => { load(); loadReco(); }, [load, loadReco]);
  useEffect(() => {
    if (!subs.some((s) => s.estado === "analizando")) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [subs, load]);

  const cambiarSector = async (v) => {
    setSector(v);
    try {
      await api.patch(`/tenants/${tid}/sector`, { sector_doctrina: v });
      toast.success("Sector actualizado. La IA analizará con esta doctrina.");
      await reload?.();
      loadReco();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length) setFiles((prev) => [...prev, ...dropped]);
  };

  const subir = async () => {
    if (files.length === 0) return toast.error("Selecciona al menos un fichero");
    setSubiendo(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      await api.post("/documentos/subir-ia", fd);
      toast.success("Ficheros en análisis…"); setFiles([]); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSubiendo(false); }
  };

  if (needsScope) return <ScopeNotice />;

  return (
    <div data-testid="subida-ia-page">
      <PageHeader overline="Nivel 1 · Documenta" title="Subida inteligente (IA)"
        action={<Button variant="outline" onClick={() => navigate("/documentos")} className="border-[#0E2A47] text-[#0E2A47]">Ir a Documentos</Button>} />

      <Card className="p-5 border-[#E2E8F0] mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="sector-activo">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#0E2A47]/50">Sector de análisis IA</span>
          {puedeEditarSector ? (
            <Select value={sector} onValueChange={cambiarSector}>
              <SelectTrigger className="h-8 w-60" data-testid="sector-select"><SelectValue placeholder="Selecciona sector" /></SelectTrigger>
              <SelectContent className="bg-white">{sectores.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Badge variant="outline" className="border-[#0E2A47]/20 text-[#0E2A47]" data-testid="sector-badge">
              {sectores.find((s) => s.value === sector)?.label || sector || "Genérico"}
            </Badge>
          )}
          <span className="text-[11px] text-[#0E2A47]/40">La IA lee tus documentos con la doctrina de este sector.</span>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          data-testid="dropzone"
          className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragOver ? "border-[#FF5625] bg-[#FF5625]/5" : "border-[#E2E8F0] bg-[#F7F8FA]"}`}
        >
          <Upload className="mx-auto text-[#FF5625]" size={28} />
          <p className="mt-2 text-sm font-semibold text-[#0E2A47]">Arrastra y suelta tus documentos aquí</p>
          <p className="text-xs text-[#0E2A47]/40 mb-3">PDF, Word o imágenes — la IA los clasifica sola, sin rellenar nada</p>
          <label className="inline-flex items-center gap-2 rounded-md bg-white border border-[#0E2A47]/20 px-4 py-2 text-sm font-medium text-[#0E2A47] cursor-pointer hover:border-[#FF5625] transition-colors">
            <FileText size={15} /> Seleccionar ficheros
            <input type="file" multiple className="hidden" data-testid="ia-files"
              onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files)])} />
          </label>
        </div>

        {files.length > 0 && (
          <div className="mt-4" data-testid="ia-file-list">
            <div className="flex flex-wrap gap-2 mb-3">
              {files.map((f, i) => (
                <Badge key={i} variant="outline" className="border-[#E2E8F0] text-[#0E2A47]/70">
                  {f.name}
                  <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="ml-2 text-[#EF4444]" data-testid={`ia-file-remove-${i}`}><X size={11} /></button>
                </Badge>
              ))}
            </div>
            <Button onClick={subir} disabled={subiendo} data-testid="ia-subir-btn" className="bg-[#FF5625] hover:bg-[#E0451A]">
              <Upload size={16} className="mr-1" />{subiendo ? "Subiendo…" : `Analizar ${files.length} con IA`}
            </Button>
          </div>
        )}
        <p className="text-xs text-[#0E2A47]/40 mt-3">Cada fichero se analiza (1 llamada IA). Nada se crea sin tu confirmación en el gate humano.</p>
        <div className="mt-2 pt-3 border-t border-[#E2E8F0]"><OnboardingAsistido variant="link" /></div>
      </Card>

      {reco?.recomendados?.length > 0 && (
        <Card className="p-5 border-[#E2E8F0] mb-6" data-testid="recomendados-card">
          <div className="flex items-center gap-2 mb-1">
            <Star className="text-[#FF5625]" size={16} />
            <h3 className="font-bold text-[#0E2A47]">Los 5 documentos que más cobertura te dan</h3>
          </div>
          <p className="text-sm text-[#0E2A47]/60 mb-4" data-testid="recomendados-copy">
            ¿Solo puedes subir uno? Empieza por tu <b className="text-[#0E2A47]">Plan APPCC</b>.
          </p>
          <div className="space-y-2">
            {reco.recomendados.map((r, i) => (
              <div key={r.clave} data-testid={`recomendado-${r.clave}`} className="flex items-center gap-3 rounded-lg border border-[#E2E8F0] p-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#0E2A47] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm text-[#0E2A47] truncate">{r.titulo}</div>
                  <div className="text-xs text-[#0E2A47]/50 truncate">{r.descripcion}</div>
                </div>
                <Badge className="shrink-0 bg-[#10B981] hover:bg-[#10B981]" data-testid={`recomendado-cobertura-${r.clave}`}>
                  cubre {r.requisitos_cubiertos} requisitos
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {subs.length === 0 && <div className="text-[#0E2A47]/50 text-sm">Bandeja vacía.</div>}
        {subs.map((s) => (
          s.estado === "analizando" ? (
            <Card key={s.id} className="p-5 border-[#E2E8F0] flex items-center gap-3" data-testid={`analizando-${s.id}`}>
              <Loader2 size={18} className="animate-spin text-[#FF5625]" />
              <span className="text-sm text-[#0E2A47]/70">Analizando {s.filename}…</span>
            </Card>
          ) : s.estado === "error" ? (
            <Card key={s.id} className="p-5 border-[#EF4444]/40" data-testid={`error-${s.id}`}>
              <div className="text-sm text-[#EF4444]">Error analizando {s.filename}</div>
              <Button size="sm" variant="outline" className="mt-2" onClick={async () => { await api.post(`/documentos/subidas/${s.id}/reanalizar`); load(); }}>Reintentar</Button>
            </Card>
          ) : (
            <Revision key={s.id} sub={s} onDone={load} nav={navigate} />
          )
        ))}
      </div>
    </div>
  );
}
